// App.js
import React, { useEffect, useState } from "react";
import { ApolloProvider } from "@apollo/client";
import { BrowserRouter as Router, useLocation } from "react-router-dom";
import createApolloClient from "./services/client";
import AppTheme from "./theme/ThemeProvider";
import { Box, CssBaseline, useTheme } from "@mui/material";
import { AppRoutes } from "./routes";
import { useAuthenticationStore } from "./stores/auth";
import {
  useImpersonatorTokenRefresh,
  useTokenRefresh,
} from "./services/useTokenRefresh";
import SideMenu from "./shared-components/SideMenu";
import Navbar from "./shared-components/Navbar";
import { SnackbarProvider } from "notistack";
import { useTrackingAnalytics } from "./hooks/useTrackingAnalytics";
import { SingleSubjectSelectorDialog } from "./shared-components/SingleSubjectSelectorDialog";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { HelpBox } from "./services/HelpBox";
import { PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { EmbeddedAssistantChat } from "./features/assistant/EmbeddedAssistantChat";
import { checkUserHasRole } from "./features/auth/Utils";

import { isHelpPath } from "./features/help/actions";

const msalInstance = new PublicClientApplication({
  auth: {
    clientId: process.env.REACT_APP_MICROSOFT_CLIENT_ID,
    authority: "https://login.microsoftonline.com/common",
    redirectUri: window.location.origin,
  },
});

const App = (props) => {
  const client = createApolloClient();
  const { user } = useAuthenticationStore();

  return (
    <ApolloProvider client={client}>
      <AppTheme {...props}>
        <CssBaseline />
        <GoogleOAuthProvider clientId="714779277988-a2i739cqtlvqugtjhdkcnpu5fgbhoq2p.apps.googleusercontent.com">
          <MsalProvider instance={msalInstance}>
            <Router>
              <AppWithTokenRefresh user={user} />
            </Router>
          </MsalProvider>
        </GoogleOAuthProvider>
      </AppTheme>
    </ApolloProvider>
  );
};

const isIndependentUserHasNoSubjects = ({ user }) => {
  if (!user?.isLoggedIn) return false;
  const info = user.info;
  if (!info) return false;
  const isIndependent =
    info.subscribed && info.subscriptionStatus === "independent";
  const teacher = info.teacher;

  const hasNoSubjects = !teacher?.subjects || teacher.subjects.length === 0;

  return isIndependent && hasNoSubjects;
};

const AppWithTokenRefresh = ({ user }) => {
  useTokenRefresh();
  useImpersonatorTokenRefresh();
  const { initPostHog } = useTrackingAnalytics();
  const theme = useTheme();
  const helpPage = isHelpPath(useLocation().pathname);
  const [subjectSelectorOpen, setSubjectSelectorOpen] = useState(false);

  useEffect(() => {
    initPostHog();
  }, []);

  useEffect(() => {
    if (!helpPage && isIndependentUserHasNoSubjects({ user })) {
      setSubjectSelectorOpen(true);
    }
  }, [user, helpPage]);

  if (helpPage) {
    return (
      <SnackbarProvider maxSnack={3}>
        <AppRoutes user={user} />
      </SnackbarProvider>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        backgroundColor: theme.palette.background.default,
      }}
    >
      <SingleSubjectSelectorDialog
        open={subjectSelectorOpen}
        setOpen={setSubjectSelectorOpen}
        user={user}
      />
      <SnackbarProvider maxSnack={3}>
        {user.isLoggedIn ? <SideMenu /> : <Navbar />}{" "}
        <Box sx={{ flexGrow: 1, minWidth: 0, ml: 2, mt: 2 }}>
          <HelpBox
            blocked={
              subjectSelectorOpen || isIndependentUserHasNoSubjects({ user })
            }
          />
          <AppRoutes user={user} />
        </Box>
        {user.isLoggedIn && checkUserHasRole({ user, role: "teacher" }) && (
          <EmbeddedAssistantChat />
        )}
      </SnackbarProvider>
    </Box>
  );
};

export { App, AppWithTokenRefresh };
